<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Report Disease Case</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Alert for Notifiable Diseases -->
          <div v-if="isNotifiableDisease" class="alert alert-danger" role="alert">
            <i class="bx bx-error-circle me-2"></i>
            <strong>Notifiable Disease!</strong> This case will be immediately reported to Woreda health office.
          </div>

          <!-- Step 1: Select Affected Person/Household -->
          <div v-if="step === 1">
            <div class="mb-4">
              <h6 class="mb-3">Select Affected Person or Household</h6>

              <!-- Case Type Selection -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="form-check card p-3"
                       :class="{'border-primary bg-light': caseType === 'individual'}">
                    <input class="form-check-input" type="radio" name="caseType"
                           id="individualCase" value="individual" v-model="caseType">
                    <label class="form-check-label ms-2" for="individualCase">
                      <strong>Individual Case</strong>
                      <br>
                      <small class="text-muted">Report for a specific person</small>
                    </label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-check card p-3"
                       :class="{'border-primary bg-light': caseType === 'household'}">
                    <input class="form-check-input" type="radio" name="caseType"
                           id="householdCase" value="household" v-model="caseType">
                    <label class="form-check-label ms-2" for="householdCase">
                      <strong>Household Outbreak</strong>
                      <br>
                      <small class="text-muted">Multiple cases in household</small>
                    </label>
                  </div>
                </div>
              </div>

              <!-- Search -->
              <div class="search-box mb-3">
                <div class="position-relative">
                  <input v-model="searchQuery" @input="search" type="text"
                         class="form-control"
                         :placeholder="caseType === 'individual' ? 'Search by name or CBHI number...' : 'Search household number...'">
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
              </div>

              <!-- Recent Contacts (for follow-up) -->
              <div v-if="recentContacts.length > 0" class="mb-4">
                <h6 class="text-muted mb-2">Recent Contacts</h6>
                <div class="list-group">
                  <div v-for="contact in recentContacts" :key="contact.id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selected?.id === contact.id}"
                       @click="selectTarget(contact)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">
                          {{ caseType === 'individual' ?
                            `${contact.first_name} ${contact.surname}` :
                            contact.household_number }}
                        </h6>
                        <p class="mb-0 text-muted">
                          <span v-if="caseType === 'individual'">
                            Age: {{ getAge(contact.date_of_birth) }} •
                            {{ contact.household_number }}
                          </span>
                          <span v-else>
                            {{ contact.member_count }} members •
                            {{ contact.household_head_name }}
                          </span>
                        </p>
                      </div>
                      <small class="text-muted">
                        <i class="bx bx-time-five"></i>
                        {{ getDaysAgo(contact.last_visit_date) }} days ago
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Search Results -->
              <div v-if="searchResults.length > 0">
                <h6 class="text-muted mb-2">Search Results</h6>
                <div class="list-group">
                  <div v-for="result in searchResults" :key="result.id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selected?.id === result.id}"
                       @click="selectTarget(result)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">
                          {{ caseType === 'individual' ?
                            `${result.first_name} ${result.surname}` :
                            result.household_number }}
                        </h6>
                        <p class="mb-0 text-muted">
                          <span v-if="caseType === 'individual'">
                            {{ result.cbhi_number || 'No CBHI' }}
                          </span>
                          <span v-else>
                            {{ result.kebele_name }}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Results -->
              <div v-else-if="searchQuery && !loading" class="text-center py-4">
                <p class="text-muted">No results found</p>
              </div>
            </div>
          </div>

          <!-- Step 2: Disease Information -->
          <div v-else-if="step === 2">
            <div class="mb-4">
              <h6 class="mb-3">Disease Information</h6>

              <!-- Selected Target Info -->
              <div class="alert alert-info">
                <div class="d-flex justify-content-between">
                  <div>
                    <h6 class="alert-heading">
                      {{ caseType === 'individual' ?
                        `${selected.first_name} ${selected.surname}` :
                        selected.household_number }}
                    </h6>
                    <p class="mb-0">
                      <span v-if="caseType === 'individual'">
                        Age: {{ getAge(selected.date_of_birth) }} •
                        {{ selected.gender }}
                      </span>
                      <span v-else>
                        {{ selected.member_count }} members
                      </span>
                    </p>
                  </div>
                  <button @click="previousStep" class="btn btn-sm btn-secondary">
                    <i class="bx bx-arrow-back"></i> Change
                  </button>
                </div>
              </div>

              <!-- Disease Selection -->
              <div class="mb-3">
                <label class="form-label">Disease Type <span class="text-danger">*</span></label>
                <select v-model="diseaseForm.diseaseType" @change="onDiseaseChange"
                        class="form-select" required>
                  <option value="">Select disease</option>
                  <optgroup label="Notifiable Diseases">
                    <option value="AFP">Acute Flaccid Paralysis (AFP)</option>
                    <option value="Measles">Measles</option>
                    <option value="Cholera">Cholera</option>
                    <option value="Meningitis">Meningitis</option>
                    <option value="Yellow Fever">Yellow Fever</option>
                    <option value="COVID-19">COVID-19</option>
                    <option value="Neonatal Tetanus">Neonatal Tetanus</option>
                  </optgroup>
                  <optgroup label="Common Diseases">
                    <option value="Malaria">Malaria</option>
                    <option value="Diarrhea">Diarrhea (non-cholera)</option>
                    <option value="Pneumonia">Pneumonia</option>
                    <option value="Typhoid">Typhoid Fever</option>
                    <option value="TB">Tuberculosis</option>
                    <option value="HIV">HIV/AIDS</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="Other">Other (specify)</option>
                  </optgroup>
                </select>
              </div>

              <!-- Other Disease Specification -->
              <div v-if="diseaseForm.diseaseType === 'Other'" class="mb-3">
                <label class="form-label">Specify Disease <span class="text-danger">*</span></label>
                <input v-model="diseaseForm.otherDisease" type="text" class="form-control"
                       placeholder="Enter disease name..." required>
              </div>

              <!-- Onset Date -->
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Onset Date <span class="text-danger">*</span></label>
                  <input v-model="diseaseForm.onsetDate" type="date" class="form-control"
                         :max="today" required>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Severity <span class="text-danger">*</span></label>
                  <select v-model="diseaseForm.severity" class="form-select" required>
                    <option value="">Select severity</option>
                    <option value="mild">Mild</option>
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>

              <!-- Symptoms -->
              <div class="mb-3">
                <label class="form-label">Symptoms <span class="text-danger">*</span></label>
                <div class="row g-2">
                  <div class="col-md-4" v-for="symptom in commonSymptoms" :key="symptom">
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox"
                             :id="'symptom_' + symptom" :value="symptom"
                             v-model="diseaseForm.symptoms">
                      <label class="form-check-label" :for="'symptom_' + symptom">
                        {{ symptom }}
                      </label>
                    </div>
                  </div>
                </div>
                <input v-model="diseaseForm.otherSymptoms" type="text"
                       class="form-control mt-2"
                       placeholder="Other symptoms...">
              </div>

              <!-- Number of Cases (for household) -->
              <div v-if="caseType === 'household'" class="mb-3">
                <label class="form-label">Number of Cases <span class="text-danger">*</span></label>
                <input v-model.number="diseaseForm.numberOfCases" type="number"
                       class="form-control" min="1" required>
              </div>
            </div>
          </div>

          <!-- Step 3: Treatment and Contact Tracing -->
          <div v-else-if="step === 3">
            <div class="mb-4">
              <h6 class="mb-3">Treatment and Contact Information</h6>

              <!-- Treatment Given -->
              <div class="mb-3">
                <label class="form-label">Treatment Given</label>
                <textarea v-model="diseaseForm.treatmentGiven" class="form-control" rows="2"
                          placeholder="Describe any treatment provided..."></textarea>
              </div>

              <!-- Referral -->
              <div class="mb-3">
                <div class="form-check">
                  <input v-model="diseaseForm.referralMade" class="form-check-input"
                         type="checkbox" id="referralCheck">
                  <label class="form-check-label" for="referralCheck">
                    Patient referred to health facility
                  </label>
                </div>
                <div v-if="diseaseForm.referralMade" class="mt-2">
                  <input v-model="diseaseForm.referralFacility" type="text"
                         class="form-control" placeholder="Facility name...">
                </div>
              </div>

              <!-- Isolation Required -->
              <div class="mb-3">
                <div class="form-check">
                  <input v-model="diseaseForm.isolationRequired" class="form-check-input"
                         type="checkbox" id="isolationCheck">
                  <label class="form-check-label" for="isolationCheck">
                    Isolation/Quarantine required
                  </label>
                </div>
              </div>

              <!-- Contact Tracing -->
              <div class="mb-3">
                <label class="form-label">Contact Tracing</label>
                <div class="row">
                  <div class="col-md-6 mb-2">
                    <label class="form-label">Contacts Identified</label>
                    <input v-model.number="diseaseForm.contactsIdentified"
                           type="number" class="form-control" min="0">
                  </div>
                  <div class="col-md-6 mb-2">
                    <label class="form-label">Contacts Traced</label>
                    <input v-model.number="diseaseForm.contactsTraced"
                           type="number" class="form-control" min="0"
                           :max="diseaseForm.contactsIdentified">
                  </div>
                </div>
              </div>

              <!-- Additional Notes -->
              <div class="mb-3">
                <label class="form-label">Additional Notes</label>
                <textarea v-model="diseaseForm.notes" class="form-control" rows="2"
                          placeholder="Any additional information..."></textarea>
              </div>

              <!-- Summary -->
              <div class="alert alert-warning">
                <h6 class="alert-heading">Report Summary</h6>
                <ul class="mb-0">
                  <li>Disease: <strong>{{ diseaseForm.diseaseType }}</strong></li>
                  <li>Severity: <strong>{{ diseaseForm.severity }}</strong></li>
                  <li>Onset: <strong>{{ formatDate(diseaseForm.onsetDate) }}</strong></li>
                  <li v-if="isNotifiableDisease" class="text-danger">
                    <strong>Will be reported to Woreda immediately</strong>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button v-if="step === 1" type="button" class="btn btn-primary"
                  @click="nextStep" :disabled="!selected">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <button v-else-if="step === 2" type="button" class="btn btn-primary"
                  @click="nextStep" :disabled="!isStep2Valid">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <div v-else-if="step === 3">
            <button type="button" class="btn btn-secondary me-2" @click="previousStep">
              <i class="bx bx-chevron-left"></i> Back
            </button>
            <button type="button" class="btn btn-danger" @click="submitReport"
                    :disabled="submitting">
              <span v-if="submitting">
                <span class="spinner-border spinner-border-sm me-1"></span> Reporting...
              </span>
              <span v-else>
                <i class="bx bx-send"></i> Submit Report
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ReportDiseaseModal",
  props: {
    preselectedMember: {
      type: Object,
      default: null
    },
    preselectedHousehold: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      step: 1,
      loading: false,
      submitting: false,

      // Case selection
      caseType: 'individual',
      searchQuery: '',
      searchResults: [],
      recentContacts: [],
      selected: null,

      // Disease form
      diseaseForm: {
        diseaseType: '',
        otherDisease: '',
        onsetDate: '',
        severity: '',
        symptoms: [],
        otherSymptoms: '',
        numberOfCases: 1,
        treatmentGiven: '',
        referralMade: false,
        referralFacility: '',
        isolationRequired: false,
        contactsIdentified: 0,
        contactsTraced: 0,
        notes: ''
      },

      // Reference data
      notifiableDiseases: ['AFP', 'Measles', 'Cholera', 'Meningitis', 'Yellow Fever', 'COVID-19', 'Neonatal Tetanus'],
      commonSymptoms: [
        'Fever', 'Cough', 'Headache', 'Vomiting', 'Diarrhea', 'Rash',
        'Difficulty Breathing', 'Body Aches', 'Fatigue', 'Loss of Appetite',
        'Abdominal Pain', 'Jaundice'
      ],

      today: new Date().toISOString().split('T')[0]
    };
  },

  computed: {
    isNotifiableDisease() {
      return this.notifiableDiseases.includes(this.diseaseForm.diseaseType);
    },

    isStep2Valid() {
      if (!this.diseaseForm.diseaseType || !this.diseaseForm.onsetDate || !this.diseaseForm.severity) {
        return false;
      }
      if (this.diseaseForm.diseaseType === 'Other' && !this.diseaseForm.otherDisease) {
        return false;
      }
      if (this.diseaseForm.symptoms.length === 0 && !this.diseaseForm.otherSymptoms) {
        return false;
      }
      if (this.caseType === 'household' && !this.diseaseForm.numberOfCases) {
        return false;
      }
      return true;
    }
  },

  mounted() {
    // Handle preselected data
    if (this.preselectedMember) {
      this.caseType = 'individual';
      this.selected = this.preselectedMember;
      this.step = 2;
    } else if (this.preselectedHousehold) {
      this.caseType = 'household';
      this.selected = this.preselectedHousehold;
      this.step = 2;
    } else {
      this.loadRecentContacts();
    }
  },

  methods: {
    async loadRecentContacts() {
      this.loading = true;

      try {
        // Load recent visits/contacts
        const data = {
          "function": "getRecentContacts",
          "type": this.caseType,
          "days": 7
        };

        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.recentContacts = res.data.data.contacts || [];
        }
      } catch (e) {
        console.error("Failed to load recent contacts:", e);
      } finally {
        this.loading = false;
      }
    },

    async search() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.searchResults = [];
        return;
      }

      this.loading = true;

      const data = {
        "function": this.caseType === 'individual' ? "searchMembers" : "getHouseholds",
        "searchTerm": this.searchQuery,
        "searchType": "all",
        "limit": 10,
        "offset": 0
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          if (this.caseType === 'individual') {
            this.searchResults = res.data.data.members || [];
          } else {
            this.searchResults = res.data.data.households || [];
          }

          // Add unique IDs
          this.searchResults = this.searchResults.map(r => ({
            ...r,
            id: this.caseType === 'individual' ? r.member_id : r.household_id
          }));
        }
      } catch (e) {
        console.error("Failed to search:", e);
      } finally {
        this.loading = false;
      }
    },

    selectTarget(target) {
      this.selected = target;
    },

    onDiseaseChange() {
      // Auto-set severity for certain diseases
      if (['Cholera', 'Meningitis', 'Yellow Fever'].includes(this.diseaseForm.diseaseType)) {
        this.diseaseForm.severity = 'severe';
        this.diseaseForm.isolationRequired = true;
      }
    },

    nextStep() {
      if (this.step === 1 && this.selected) {
        this.step = 2;
      } else if (this.step === 2 && this.isStep2Valid) {
        this.step = 3;
      }
    },

    previousStep() {
      if (this.step > 1) {
        this.step--;
      }
    },

    async submitReport() {
      this.submitting = true;

      // Prepare symptoms string
      const allSymptoms = [
        ...this.diseaseForm.symptoms,
        ...(this.diseaseForm.otherSymptoms ? [this.diseaseForm.otherSymptoms] : [])
      ].join(', ');

      const data = {
        "function": "reportDiseaseCase",
        "memberId": this.caseType === 'individual' ? this.selected.id : null,
        "householdId": this.caseType === 'household' ? this.selected.id :
            (this.selected.household_id || null),
        "diseaseType": this.diseaseForm.diseaseType === 'Other' ?
            this.diseaseForm.otherDisease : this.diseaseForm.diseaseType,
        "diseaseCode": this.getDiseaseCode(),
        "onsetDate": this.diseaseForm.onsetDate,
        "symptoms": allSymptoms,
        "severity": this.diseaseForm.severity,
        "treatmentGiven": this.diseaseForm.treatmentGiven,
        "isolationRequired": this.diseaseForm.isolationRequired,
        "contactsTraced": this.diseaseForm.contactsTraced
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // If referral was made, create referral record
          if (this.diseaseForm.referralMade && this.caseType === 'individual') {
            await this.createReferral(res.data.data.surveillanceId);
          }

          this.$emit('reported', {
            surveillanceId: res.data.data.surveillanceId,
            notifiable: res.data.data.reportedToWoreda
          });
        } else {
          alert(res.data.message || 'Failed to report disease case');
        }
      } catch (e) {
        console.error("Failed to submit report:", e);
        alert('Failed to submit report. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    async createReferral(surveillanceId) {
      if (!this.diseaseForm.referralFacility || this.caseType !== 'individual') return;

      const data = {
        "function": "createReferral",
        "memberId": this.selected.id,
        "referralType": "disease_case",
        "referralReason": `${this.diseaseForm.diseaseType} - ${this.diseaseForm.severity}`,
        "referralTo": this.diseaseForm.referralFacility,
        "urgency": this.diseaseForm.severity === 'critical' ? 'urgent' :
            this.diseaseForm.severity === 'severe' ? 'high' : 'routine',
        "clinicalNotes": `Disease surveillance ID: ${surveillanceId}. ${this.diseaseForm.notes}`
      };

      try {
        await executeJson(data, authUrl);
      } catch (e) {
        console.error("Failed to create referral:", e);
      }
    },

    getDiseaseCode() {
      const codes = {
        'AFP': 'A80',
        'Measles': 'B05',
        'Cholera': 'A00',
        'Meningitis': 'G03',
        'Yellow Fever': 'A95',
        'COVID-19': 'U07.1',
        'Malaria': 'B50',
        'Diarrhea': 'A09',
        'Pneumonia': 'J18',
        'Typhoid': 'A01.0',
        'TB': 'A15',
        'HIV': 'B20'
      };
      return codes[this.diseaseForm.diseaseType] || 'R69';
    },

    getAge(dateOfBirth) {
      if (!dateOfBirth) return 'Unknown';

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        return age - 1 + ' years';
      }

      return age + ' years';
    },

    getDaysAgo(date) {
      if (!date) return 'Never';
      const days = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
      return days;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  },

  watch: {
    caseType() {
      // Reset when switching between individual and household
      this.selected = null;
      this.searchQuery = '';
      this.searchResults = [];
      this.loadRecentContacts();
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #999;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: #556ee6;
  border-color: #556ee6;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.form-check.card {
  cursor: pointer;
  transition: all 0.2s;
}

.form-check.card:hover {
  border-color: #556ee6;
}

.form-check.card.border-primary {
  background-color: rgba(85, 110, 230, 0.05) !important;
}
</style>